import { cn, FOCUS_RING } from "./cn";

const ELLIPSIS = "…";

export function getPageItems(
  currentPage: number,
  totalPages: number,
  windowSize = 5,
): (number | typeof ELLIPSIS)[] {
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, ELLIPSIS, totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, ELLIPSIS, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, ELLIPSIS, currentPage - 1, currentPage, currentPage + 1, ELLIPSIS, totalPages];
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  prevLabel?: string;
  nextLabel?: string;
  hideWhenSingle?: boolean;
  className?: string;
}

const CELL = cn(
  "inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors",
  FOCUS_RING,
);

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  prevLabel = "Anterior",
  nextLabel = "Siguiente",
  hideWhenSingle = true,
  className,
}: PaginationProps) {
  if (hideWhenSingle && totalPages <= 1) return null;

  return (
    <nav
      aria-label="Paginación"
      className={cn("flex flex-wrap items-center justify-center gap-1.5", className)}
    >
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        aria-label={prevLabel}
        className={cn(
          CELL,
          "border border-line text-content-secondary hover:bg-subtle hover:text-content disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
        )}
      >
        <i className="ri-arrow-left-s-line sm:hidden" />
        <span className="hidden sm:inline">{prevLabel}</span>
      </button>

      {getPageItems(currentPage, totalPages).map((item, index) =>
        item === ELLIPSIS ? (
          <span key={`gap-${index}`} className={cn(CELL, "text-content-faint")}>
            {ELLIPSIS}
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            aria-current={item === currentPage ? "page" : undefined}
            className={cn(
              CELL,
              item === currentPage
                ? "bg-primary-500 text-white shadow-sm"
                : "border border-line text-content-secondary hover:bg-subtle hover:text-content",
            )}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        aria-label={nextLabel}
        className={cn(
          CELL,
          "border border-line text-content-secondary hover:bg-subtle hover:text-content disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
        )}
      >
        <i className="ri-arrow-right-s-line sm:hidden" />
        <span className="hidden sm:inline">{nextLabel}</span>
      </button>
    </nav>
  );
}
